import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Rating, LinearProgress, Avatar, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Divider, IconButton, Paper, Grid, Alert
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const ProductReviews = ({ productId, productName }) => {
  const { t } = useTranslation('product');
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [writeDialogOpen, setWriteDialogOpen] = useState(false);
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: '',
    comment: '',
    customerName: '',
    customerEmail: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [helpfulVotes, setHelpfulVotes] = useState({});

  // 載入評論
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await api.get(`/reviews/product/${productId}`);
        if (response.data.success) {
          setReviews(response.data.reviews || []);
          setSummary(response.data.summary || null);
        }
      } catch (error) {
        console.error('載入評論失敗:', error);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchReviews();
    }
  }, [productId]);

  // 提交評論
  const handleSubmitReview = async () => {
    if (!newReview.rating || !newReview.comment) {
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/reviews', {
        product_id: productId,
        ...newReview
      });
      setWriteDialogOpen(false);
      setNewReview({ rating: 5, title: '', comment: '', customerName: '', customerEmail: '' });
      // 重新載入評論
      const response = await api.get(`/reviews/product/${productId}`);
      if (response.data.success) {
        setReviews(response.data.reviews || []);
        setSummary(response.data.summary || null);
      }
    } catch (error) {
      console.error('提交評論失敗:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 標記有幫助
  const handleHelpful = async (reviewId) => {
    if (helpfulVotes[reviewId]) return;

    try {
      await api.post(`/reviews/${reviewId}/helpful`);
      setHelpfulVotes(prev => ({ ...prev, [reviewId]: true }));
      setReviews(prev => prev.map(r =>
        r.review_id === reviewId ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r
      ));
    } catch (error) {
      console.error('標記有幫助失敗:', error);
    }
  };

  // 格式化日期
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 評分分佈
  const RatingDistribution = () => {
    if (!summary) return null;

    const ratings = [
      { stars: 5, count: summary.rating_5_count || 0 },
      { stars: 4, count: summary.rating_4_count || 0 },
      { stars: 3, count: summary.rating_3_count || 0 },
      { stars: 2, count: summary.rating_2_count || 0 },
      { stars: 1, count: summary.rating_1_count || 0 }
    ];

    const total = summary.total_reviews || 1;

    return (
      <Box sx={{ width: '100%' }}>
        {ratings.map((r) => (
          <Box key={r.stars} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body2" sx={{ width: 20 }}>{r.stars}</Typography>
            <StarIcon sx={{ fontSize: 16, color: '#faaf00' }} />
            <LinearProgress
              variant="determinate"
              value={(r.count / total) * 100}
              sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: '#eee' }}
            />
            <Typography variant="body2" sx={{ width: 30, textAlign: 'right' }}>
              {r.count}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  if (loading) {
    return <Box sx={{ py: 4, textAlign: 'center' }}>{t('reviews.loading')}</Box>;
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {t('reviews.title')}
      </Typography>

      {/* 評論摘要 */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" fontWeight="bold" color="primary">
                {summary?.average_rating?.toFixed(1) || '0.0'}
              </Typography>
              <Rating
                value={parseFloat(summary?.average_rating) || 0}
                precision={0.1}
                readOnly
                size="large"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('reviews.review_count', { count: summary?.total_reviews || 0 })}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <RatingDistribution />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setWriteDialogOpen(true)}
              >
                {t('reviews.write_review')}
              </Button>
              {summary?.verified_purchase_count > 0 && (
                <Chip
                  icon={<VerifiedIcon />}
                  label={t('reviews.verified_count', { count: summary.verified_purchase_count })}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 評論列表 */}
      {reviews.length === 0 ? (
        <Alert severity="info">
          {t('reviews.no_reviews')}
        </Alert>
      ) : (
        <Box>
          {reviews.map((review, index) => (
            <Box key={review.review_id}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {review.customer_name?.charAt(0) || 'U'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {review.customer_name || t('reviews.anonymous')}
                    </Typography>
                    {review.verified_purchase && (
                      <Chip
                        icon={<VerifiedIcon />}
                        label={t('reviews.verified_purchase')}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Rating value={review.rating} readOnly size="small" />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(review.created_at)}
                    </Typography>
                  </Box>
                  {review.title && (
                    <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                      {review.title}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {review.comment}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleHelpful(review.review_id)}
                      disabled={helpfulVotes[review.review_id]}
                      color={helpfulVotes[review.review_id] ? 'primary' : 'default'}
                    >
                      {helpfulVotes[review.review_id] ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                    </IconButton>
                    <Typography variant="caption" color="text.secondary">
                      {t('reviews.helpful_count', { count: review.helpful_count || 0 })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* 撰寫評論對話框 */}
      <Dialog open={writeDialogOpen} onClose={() => setWriteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('reviews.write_review')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('reviews.rating')}
            </Typography>
            <Rating
              value={newReview.rating}
              onChange={(e, v) => setNewReview(prev => ({ ...prev, rating: v }))}
              size="large"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('reviews.your_name')}
              value={newReview.customerName}
              onChange={(e) => setNewReview(prev => ({ ...prev, customerName: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('reviews.email_private')}
              type="email"
              value={newReview.customerEmail}
              onChange={(e) => setNewReview(prev => ({ ...prev, customerEmail: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('reviews.title_optional')}
              value={newReview.title}
              onChange={(e) => setNewReview(prev => ({ ...prev, title: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label={t('reviews.comment_content')}
              multiline
              rows={4}
              value={newReview.comment}
              onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWriteDialogOpen(false)}>{t('reviews.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSubmitReview}
            disabled={submitting || !newReview.comment}
          >
            {submitting ? t('reviews.submitting') : t('reviews.submit_review')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductReviews;
